/**
 * Standalone API client for the B2B Tier System.
 *
 * Lives in its own file (not in services/api.ts) so this PR does not touch
 * the shared api.ts namespace per the worktree's HARD RULE. Imports
 * directly from this file.
 */

const API_PREFIX = "/api/b2b-tiers";

async function jsonFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_PREFIX}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`b2b_tiers ${path} → ${res.status}: ${text}`);
  }
  return (await res.json()) as T;
}

export interface TierRule {
  slug: string;
  display_name: string;
  discount_pct: number;
  min_monthly_aed: number;
  min_order_count: number;
  requires_trn: boolean;
  sort_order: number;
  description?: string | null;
}

export interface TierPricingRule {
  id: string;
  sku: string;
  tier_slug: string;
  discount_pct: number;
  fixed_price_aed?: number | null;
  notes?: string | null;
}

export interface CustomerTier {
  id: string;
  lead_id: string;
  tier_slug: string;
  classification: "auto" | "manual" | "override";
  classifier_score: number | null;
  classifier_reasons: string[];
  manual_override_by?: string | null;
  manual_override_at?: string | null;
  notes?: string | null;
}

export interface PriceQuote {
  sku: string;
  tier_slug: string;
  retail_price: number;
  discount_pct: number;
  fixed_price_aed: number | null;
  final_price_aed: number;
  rule_source: "sku_override" | "tier_default" | "service_no_discount" | "no_tier";
}

export interface ModuleStatus {
  module: string;
  version: string;
  tier_rule_count: number;
  customer_tier_count: number;
  sku_override_count: number;
  expected_tiers: string[];
  default_count: number;
}

export const b2bTiersApi = {
  status: () => jsonFetch<ModuleStatus>("/status"),

  listTierRules: () => jsonFetch<TierRule[]>("/tier-rules"),

  updateTierRule: (slug: string, patch: Partial<TierRule>) =>
    jsonFetch<TierRule>(`/tier-rules/${encodeURIComponent(slug)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  listSkuRules: (params: { tier_slug?: string; sku?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.tier_slug) qs.set("tier_slug", params.tier_slug);
    if (params.sku) qs.set("sku", params.sku);
    return jsonFetch<TierPricingRule[]>(
      `/tier-pricing-rules${qs.toString() ? `?${qs}` : ""}`,
    );
  },

  createSkuRule: (rule: Omit<TierPricingRule, "id">) =>
    jsonFetch<TierPricingRule>("/tier-pricing-rules", {
      method: "POST",
      body: JSON.stringify(rule),
    }),

  deleteSkuRule: (id: string) =>
    jsonFetch<{ deleted: string }>(
      `/tier-pricing-rules/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),

  listCustomerTiers: (params: {
    tier_slug?: string;
    classification?: string;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.tier_slug) qs.set("tier_slug", params.tier_slug);
    if (params.classification) qs.set("classification", params.classification);
    return jsonFetch<CustomerTier[]>(
      `/customer-tiers${qs.toString() ? `?${qs}` : ""}`,
    );
  },

  setLeadTier: (lead_id: string, tier_slug: string, overridden_by = "admin", notes?: string) =>
    jsonFetch<CustomerTier>(`/lead/${encodeURIComponent(lead_id)}/tier`, {
      method: "POST",
      body: JSON.stringify({ tier_slug, overridden_by, notes }),
    }),

  reclassifyLead: (lead_id: string) =>
    jsonFetch<CustomerTier>(`/lead/${encodeURIComponent(lead_id)}/reclassify`, {
      method: "POST",
    }),

  quoteForTier: (sku: string, retail_price: number, tier_slug: string) =>
    jsonFetch<PriceQuote>("/price/quote", {
      method: "POST",
      body: JSON.stringify({ sku, retail_price, tier_slug }),
    }),

  quoteForLead: (sku: string, retail_price: number, lead_id: string) =>
    jsonFetch<PriceQuote>("/price/lead-quote", {
      method: "POST",
      body: JSON.stringify({ sku, retail_price, lead_id }),
    }),
};
